import { BaseService } from '@common/http-layer/service/base.service'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { EventSearchQuery, EventSearchResult, EventFormDataType, MeetingType, MeetingCreateResponse } from '../types/events'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Events service - handles all events-related API calls
 * Base URL: COMMUNITIES_SERVER_URL
 * Base Path: /{locationId}/groups/{groupId}/events
 */
export class EventsService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '', // We'll build the path dynamically with locationId and groupId
    })
  }

  /**
   * Get location and group-aware base path
   */
  private getBasePath(locationId: string, groupId: string): string {
    if (!locationId) throw new Error('Location ID is required')
    if (!groupId) throw new Error('Group ID is required')

    return `/${locationId}/groups/${groupId}/events`
  }

  /**
   * Get a single event by ID.
   * Path: /{locationId}/groups/{groupId}/events/{eventId}
   *
   * Backend resolves the next recurring occurrence (`nextOccurrence`, shifted start/end).
   * `query.occurrenceDate` is accepted for backward compatibility but ignored by the API —
   * callers should not pass it.
   */
  async getEventById(
    eventId: string,
    groupId: string,
    locationId: string,
    query?: { occurrenceDate?: string }
  ): Promise<EventSearchResult> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get(`${groupPath}/${eventId}`, {
      params: query,
      groupId,
      locationId,
    })
    return data as EventSearchResult
  }

  /**
   * Search for taggable events
   * Used for mentions in editor
   */
  async searchTaggableEvents(query: EventSearchQuery & { groupId: string; locationId: string }): Promise<EventSearchResult[]> {
    const groupPath = this.getBasePath(query.locationId, query.groupId)
    const params: Record<string, string> = {}
    if (query.search) params.search = query.search
    if (query.startDate) params.startDate = query.startDate
    if (query.endDate) params.endDate = query.endDate

    const { data } = await this.get(`${groupPath}/search`, {
      params: {
        ...(query.search && { search: query.search }),
        ...(query.startDate && { startDate: query.startDate }),
        ...(query.endDate && { endDate: query.endDate }),
      },
      groupId: query.groupId,
      locationId: query.locationId,
    })
    return (data as EventSearchResult[]) || []
  }

  async getCalendarEvents(query: EventSearchQuery & { groupId: string; locationId: string }): Promise<EventSearchResult[]> {
    const groupPath = this.getBasePath(query.locationId, query.groupId)
    const { data } = await this.get(groupPath, {
      params: {
        ...(query.startDate && { startDate: query.startDate }),
        ...(query.endDate && { endDate: query.endDate }),
      },
      groupId: query.groupId,
      locationId: query.locationId,
    })
    return (data as EventSearchResult[]) || []
  }

  async fetchPastAndUpcomingEvents(query: any): Promise<any> {
    const groupPath = this.getBasePath(query.locationId, query.groupId)
    // Get user's timezone or guess it
    const localTimezone = dayjs.tz.guess()

    // Current time in local timezone with proper timezone info
    const currentTime = dayjs().tz(localTimezone)

    const { data } = await this.get(`${groupPath}/past-upcoming`, {
      params: {
        currentDate: currentTime.utc().format(),
        limit: 6,
      },
      groupId: query.groupId,
      locationId: query.locationId,
    })
    return (data as EventSearchResult[]) || []
  }

  /**
   * Get RSVP count for an event
   * Path: /{locationId}/groups/{groupId}/rsvp/{eventId}/count
   */
  async getEventRSVPCount(query: { groupId: string; locationId: string; eventId: string }): Promise<number> {
    const { data } = await this.get(`/${query.locationId}/groups/${query.groupId}/rsvp/${query.eventId}/count`, {
      groupId: query.groupId,
      locationId: query.locationId,
    })
    return (data as number) || 0
  }

  /**
   * Get attendees list for an event
   * Path: /{locationId}/groups/{groupId}/rsvp/{eventId}/attendees
   */
  async getEventAttendees(query: { groupId: string; locationId: string; eventId: string; limit?: number }): Promise<any> {
    console.log('getEventAttendees service', query)
    const { data } = await this.get(`/${query.locationId}/groups/${query.groupId}/rsvp/${query.eventId}/attendees`, {
      params: {
        limit: query.limit || 20,
      },
      groupId: query.groupId,
      locationId: query.locationId,
    })
    return data || { attendees: [] }
  }

  /**
   * Export attendees for an event
   * Path: /{locationId}/groups/{groupId}/events/{eventId}/export-attendees
   */
  async exportEventAttendees(query: { groupId: string; locationId: string; eventId: string }): Promise<any> {
    const groupPath = this.getBasePath(query.locationId, query.groupId)
    const { data } = await this.get(`${groupPath}/${query.eventId}/export-attendees`, {
      groupId: query.groupId,
      locationId: query.locationId,
    })
    return data
  }

  /**
   * Create or update RSVP for an event
   * Path: /{locationId}/groups/{groupId}/rsvp/{eventId}
   */
  async updateEventRSVP(query: {
    groupId: string
    locationId: string
    eventId: string
    status: 'ATTENDING' | 'DECLINED' | 'MAYBE'
    paymentMeta?: {
      paymentStatus: string
    }
  }): Promise<any> {
    const { data } = await this.post(
      `/${query.locationId}/groups/${query.groupId}/rsvp/${query.eventId}`,
      {
        eventId: query.eventId,
        status: query.status,
        paymentMeta: query.paymentMeta || { paymentStatus: 'FREE' },
      },
      {
        groupId: query.groupId,
        locationId: query.locationId,
      }
    )
    return data
  }

  async createEvent(variables: { groupId: string; locationId: string; formData: EventFormDataType }): Promise<any> {
    const groupPath = this.getBasePath(variables.locationId, variables.groupId)
    const { data } = await this.post(groupPath, variables.formData, {
      groupId: variables.groupId,
      locationId: variables.locationId,
    })
    return data
  }

  async createMeeting(variables: {
    groupId: string
    locationId: string
    title: string
    type: MeetingType
  }): Promise<MeetingCreateResponse> {
    const groupPath = this.getBasePath(variables.locationId, variables.groupId)
    const { data } = await this.post(
      `${groupPath}/meetings`,
      {
        title: variables.title,
        type: variables.type,
        groupId: variables.groupId,
      },
      {
        groupId: variables.groupId,
        locationId: variables.locationId,
      }
    )
    return data as MeetingCreateResponse
  }

  async updateEvent(variables: { groupId: string; locationId: string; eventId: string; formData: EventFormDataType }): Promise<any> {
    const groupPath = this.getBasePath(variables.locationId, variables.groupId)
    const finalPath = `${groupPath}/${variables.eventId}`
    const { data } = await this.patch(finalPath, variables.formData, {
      groupId: variables.groupId,
      locationId: variables.locationId,
    })
    return data
  }

  async deleteEvent(variables: { groupId: string; locationId: string; eventId: string; deleteParams: any }): Promise<any> {
    const groupPath = this.getBasePath(variables.locationId, variables.groupId)
    const finalPath = `${groupPath}/${variables.eventId}`
    const { data } = await this.delete(finalPath, {
      params: variables.deleteParams,
      groupId: variables.groupId,
      locationId: variables.locationId,
    })
    return data
  }

  /**
   * Add event to calendar (Google Calendar, iCal, Outlook, etc.)
   * Path: /{locationId}/groups/{groupId}/events/{eventId}/add-to-calendar/{calendar}
   * @param calendar - Calendar type: 'google', 'ical', 'outlook', 'apple', etc.
   */
  async addEventToCalendar(variables: { groupId: string; locationId: string; eventId: string; calendar: string }): Promise<any> {
    const groupPath = this.getBasePath(variables.locationId, variables.groupId)
    const finalPath = `${groupPath}/${variables.eventId}/add-to-calendar/${variables.calendar}`
    const { data } = await this.get(finalPath, {
      groupId: variables.groupId,
      locationId: variables.locationId,
    })
    return data
  }
}
